import { useState, type CSSProperties, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from './icons/Icon'

/**
 * Every business-entity kind this component knows a generic fallback label for (CLAUDE.md
 * UX-quality pass — "never show a bare UUID"). Adding a new kind here is a one-line addition, not
 * a schema/module-boundary change.
 */
export type EntityKind =
  | 'account'
  | 'legalEntity'
  | 'contact'
  | 'branch'
  | 'case'
  | 'production'
  | 'program'
  | 'programFiling'
  | 'task'
  | 'vendor'
  | 'buyer'
  | 'broker'
  | 'loanOut'
  | 'user'
  | 'fiscalYear'
  | 'record'
  | 'formTemplate'

const KIND_FALLBACK_LABEL: Record<EntityKind, string> = {
  account: 'Account',
  legalEntity: 'Legal entity',
  contact: 'Contact',
  branch: 'Branch',
  case: 'Case',
  production: 'Production',
  program: 'Program',
  programFiling: 'Filing',
  task: 'Task',
  vendor: 'Vendor',
  buyer: 'Buyer',
  broker: 'Broker',
  loanOut: 'Loan-out company',
  user: 'User',
  fiscalYear: 'Fiscal year',
  record: 'Record',
  formTemplate: 'Form',
}

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}

export interface EntityRefProps {
  kind: EntityKind
  /** `null`/`undefined` renders a plain "—" (no entity to reference at all). */
  id: string | null | undefined
  /**
   * The already-resolved business reference (Account/Legal entity name, Production title, a
   * derived "{Account} · {Production} · {FY}" Case reference, Program `code` + jurisdiction, …).
   * `null`/`undefined`/blank means "not resolved yet" (or unresolvable) — renders a generic
   * "<Kind> <shortId>" fallback, never a fabricated name and never the bare full UUID either.
   */
  name?: string | null | undefined
  /** Renders as a router `Link` when present. */
  href?: string | null | undefined
  className?: string
  style?: CSSProperties
  /** Fires on the `Link` click only (never the copy-id button) — e.g. closing a host overlay/modal on navigate. */
  onNavigate?: () => void
}

/**
 * The one place a business-entity reference is rendered (CLAUDE.md UX-quality pass) — the
 * meaningful reference (name/code/derived label) is always the primary, visible text; the raw
 * UUID is demoted to a `title` tooltip plus a small click-to-copy affordance, never shown in full
 * as the headline. When no name is resolved yet, falls back to a generic "<Kind> <shortId>" label
 * (still never the bare 36-character UUID) rather than blocking on the lookup or fabricating one.
 */
export function EntityRef({ kind, id, name, href, className, style, onNavigate }: EntityRefProps) {
  const [copied, setCopied] = useState(false)

  if (!id) {
    return <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>
  }

  const display = name?.trim() ? name.trim() : `${KIND_FALLBACK_LABEL[kind]} ${truncateId(id)}`
  const kindNoun = KIND_FALLBACK_LABEL[kind].toLowerCase()

  function handleCopy(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    if (typeof navigator === 'undefined' || !navigator.clipboard) return
    void navigator.clipboard.writeText(id as string).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }

  const primary = href ? (
    <Link
      to={href}
      className={className}
      style={{ color: 'var(--iw-accent)', textDecoration: 'none', ...style }}
      title={id}
      onClick={onNavigate}
    >
      {display}
    </Link>
  ) : (
    <span className={className} title={id} style={style}>
      {display}
    </span>
  )

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      {primary}
      <button
        type="button"
        className="iw-btn iw-btn--sm"
        style={{ padding: '0 3px', lineHeight: 1, minHeight: 0, flexShrink: 0 }}
        onClick={handleCopy}
        title={id}
        aria-label={copied ? `Copied ${kindNoun} ID to clipboard` : `Copy ${kindNoun} ID`}
      >
        <Icon name={copied ? 'check' : 'copy'} size={11} />
      </button>
    </span>
  )
}
